/**
 * Synthesis Share — chat-response share-link API helpers.
 *
 * Ports v1 ``ando-frontend/app/api/chatShares.ts``. BE surface in
 * ``ando-agent/src/api/v1/endpoints/chat_shares.py``:
 *
 *   POST   /chat/shares/preview             card + exposure, nothing written
 *   POST   /chat/shares                     create (org_members / email / public_link;
 *                                           scope message | conversation)
 *   GET    /chat/shares/mine                shares owned by caller (paginated)
 *   GET    /chat/shares/received            in-app shares received by caller
 *   GET    /chat/shares/{share_id}          detail with frozen turns (owner / recipient)
 *   POST   /chat/shares/{share_id}/continue copy the turns into a new conversation
 *   DELETE /chat/shares/{share_id}          revoke (owner only)
 *
 *   GET    /public/shares/{token}           unauthenticated public view
 *
 * The public endpoint MUST bypass the cookie-auth ``apiClient`` (a guest
 * visitor has no session) and skips the 401-refresh / device-fingerprint
 * pipeline — see ``getPublicShare`` below.
 */

import { apiClient } from "../ApiClient.js";
import { getEnv } from "../../env.js";

// ---------------------------------------------------------------------------
// Wire types — keep snake_case (BE-canonical).
// ---------------------------------------------------------------------------

export type ChatShareMode = "org_members" | "email" | "public_link";

/** One frozen answer vs the whole thread up to the share moment (Sep 2026). */
export type ChatShareScope = "message" | "conversation";

/** What the share reveals — computed BE-side before the row exists. */
export interface ChatShareExposureAttachment {
  name: string;
  kind: "file" | "image";
  mime_type: string | null;
  size_bytes: number | null;
}

export interface ChatShareExposure {
  documents: ReadonlyArray<{ label: string; source_type: string }>;
  document_count: number;
  attachments: ReadonlyArray<ChatShareExposureAttachment>;
  attachment_count: number;
  display_count: number;
  turn_count: number;
}

/** Reader-safe frozen turn: labels only, no ids into the owner's tenant. */
export interface ChatShareTurn {
  role: "user" | "assistant";
  content: string;
  query_id?: string | null;
  timestamp?: string | null;
  sources?: ReadonlyArray<{ label: string; source_type: string }>;
  attachments?: ReadonlyArray<string>;
}

export interface ChatShare {
  id: string;
  share_token: string;
  /** Absolute public URL — BE augments the row with ``FRONTEND_URL`` prefix. */
  share_url: string;
  share_mode: ChatShareMode;
  share_scope: ChatShareScope;
  owner_user_id: string;
  message_id: string | null;
  conversation_id: string | null;
  query_text: string | null;
  response_md: string;
  response_metadata: Record<string, unknown>;
  /** Share card — LLM-generated, possibly edited by the owner. */
  title: string | null;
  summary: string | null;
  exposure: ChatShareExposure;
  turn_count: number;
  recipient_user_id: string | null;
  recipient_email: string | null;
  /** First in-app open by the addressed colleague (``null`` = not yet). */
  recipient_seen_at: string | null;
  expires_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  is_revoked: boolean;
  revoked_at: string | null;
  created_at: string;
}

/** ``GET /chat/shares/{id}`` — list row + frozen turns + who shared. */
export interface ChatShareDetail extends ChatShare {
  turns: ChatShareTurn[];
  owner_name: string | null;
}

export interface ChatShareListResponse {
  items: ChatShare[];
  total: number;
}

export interface ChatShareTarget {
  share_scope: ChatShareScope;
  /** Required when ``share_scope === "message"``. */
  message_id?: string;
  /** Required when ``share_scope === "conversation"``. */
  conversation_id?: string;
}

export interface CreateChatShareRequest extends ChatShareTarget {
  share_mode: ChatShareMode;
  recipient_user_id?: string;
  recipient_email?: string;
  notify_email?: boolean;
  /** Card as shown (and possibly edited) in the dialog; BE generates when omitted. */
  title?: string;
  summary?: string;
  /** 24/168/720/null; BE caps at 8760h (1y). ``null`` = never (org/email);
   *  omitted on public_link = 168h (7d). */
  expires_in_hours?: number | null;
}

export type ChatSharePreviewRequest = ChatShareTarget;

export interface ChatSharePreviewResponse {
  share_scope: ChatShareScope;
  title: string;
  summary: string | null;
  /** True when the card came from the model (vs the deterministic excerpt). */
  generated: boolean;
  exposure: ChatShareExposure;
  turn_count: number;
}

export interface ContinueChatShareResponse {
  conversation_id: string;
  title: string;
  turn_count: number;
}

export interface PublicChatShareResponse {
  share_scope: ChatShareScope;
  /** Email-addressed share without a valid access token: no content, only
   *  ``recipient_hint`` — the page asks for the address and mails a code. */
  locked: boolean;
  recipient_hint: string | null;
  title: string | null;
  summary: string | null;
  response_md: string;
  query_text: string | null;
  response_metadata: Record<string, unknown>;
  turns: ChatShareTurn[];
  shared_by_name: string | null;
  shared_at: string | null;
  expires_at: string | null;
}

// ---------------------------------------------------------------------------
// Authenticated endpoints (JWT via httpOnly cookie)
// ---------------------------------------------------------------------------

const BASE = "/chat/shares";

export async function createChatShare(
  body: CreateChatShareRequest,
): Promise<ChatShare> {
  return apiClient.post<ChatShare>(BASE, body);
}

/** Card + exposure for a share that doesn't exist yet — nothing is written. */
export async function previewChatShare(
  body: ChatSharePreviewRequest,
): Promise<ChatSharePreviewResponse> {
  return apiClient.post<ChatSharePreviewResponse>(`${BASE}/preview`, body);
}

/** Rail badge: received shares never opened in-app by the caller. */
export async function getUnseenReceivedSharesCount(): Promise<{ unseen_count: number }> {
  return apiClient.get<{ unseen_count: number }>(`${BASE}/received/unseen-count`);
}

/** The addressed recipient opened the share in-app (idempotent). */
export async function markChatShareSeen(shareId: string): Promise<void> {
  await apiClient.post<void>(`${BASE}/${encodeURIComponent(shareId)}/seen`, {});
}

/** Owner or addressed recipient only (404 otherwise, 410 when dead). */
export async function getChatShare(shareId: string): Promise<ChatShareDetail> {
  return apiClient.get<ChatShareDetail>(`${BASE}/${encodeURIComponent(shareId)}`);
}

/** "Continua da qui" — copies the frozen turns into a new conversation of the caller. */
export async function continueChatShare(
  shareId: string,
): Promise<ContinueChatShareResponse> {
  return apiClient.post<ContinueChatShareResponse>(
    `${BASE}/${encodeURIComponent(shareId)}/continue`,
    {},
  );
}

export async function listMyShares(
  limit = 50,
  offset = 0,
): Promise<ChatShareListResponse> {
  return apiClient.get<ChatShareListResponse>(
    `${BASE}/mine?limit=${limit}&offset=${offset}`,
  );
}

export async function listReceivedShares(
  limit = 50,
  offset = 0,
): Promise<ChatShareListResponse> {
  return apiClient.get<ChatShareListResponse>(
    `${BASE}/received?limit=${limit}&offset=${offset}`,
  );
}

export async function revokeShare(shareId: string): Promise<void> {
  await apiClient.delete<void>(`${BASE}/${encodeURIComponent(shareId)}`);
}

// ---------------------------------------------------------------------------
// Public endpoint — no auth, no cookies.
//
// The visitor on /share/{token} does NOT have an ``ando_session`` cookie.
// We deliberately skip ``credentials: 'include'`` and the refresh/retry
// pipeline so the request is exactly what the BE's public_router expects.
// Status mapping (BE):
//   404            → notfound
//   410 "revoked"  → revoked
//   410 "expired"  → expired
//   other          → error
// ---------------------------------------------------------------------------

export type ChatShareErrorReason = "notfound" | "revoked" | "expired" | "error";

export class ChatShareError extends Error {
  readonly status: number;
  readonly reason: ChatShareErrorReason;

  constructor(message: string, status: number, reason: ChatShareErrorReason) {
    super(message);
    this.name = "ChatShareError";
    this.status = status;
    this.reason = reason;
  }
}

export async function getPublicShare(
  token: string,
): Promise<PublicChatShareResponse> {
  const base = getEnv().apiUrl;
  const url = `${base}/public/shares/${encodeURIComponent(token)}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      // No credentials — public by design.
    });
  } catch (err) {
    throw new ChatShareError(
      err instanceof Error ? err.message : "Network error",
      0,
      "error",
    );
  }

  if (res.ok) {
    return (await res.json()) as PublicChatShareResponse;
  }

  let detail = "";
  try {
    const body = (await res.json()) as { detail?: string };
    detail = body?.detail ?? "";
  } catch {
    // not JSON — keep detail empty
  }

  if (res.status === 404) {
    throw new ChatShareError(detail || "Share not found", 404, "notfound");
  }
  if (res.status === 410) {
    const lower = detail.toLowerCase();
    const reason: ChatShareErrorReason = lower.includes("revoke")
      ? "revoked"
      : "expired";
    throw new ChatShareError(
      detail || "Share is no longer available",
      410,
      reason,
    );
  }
  throw new ChatShareError(
    detail || `Request failed with status ${res.status}`,
    res.status,
    "error",
  );
}
